import React, { useState, useEffect } from "react";
import SEO from "../seo";

const FraudPrevention = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "where-to-find",
        "available-protections",
        "block-countries",
        "block-ip",
        "max-items",
        "cod-delay",
        "ip-tracking",
        "rule-triggered",
        "best-practices",
        "faq",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Fraud Prevention | xPage Help Center"
        description="Learn how to protect your store from fake orders, spam submissions, abusive customers, and high-risk traffic using xPage Fraud Prevention tools."
        keywords="xPage fraud prevention, block countries, block IP, COD abuse, fake orders, order protection, fraud detection"
        canonical="https://help.xpage.ai/help/settings/FraudPrevention"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">

        {/* Header / Overview */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Fraud Prevention
          </h1>
          <p className="text-sm text-systemLabel">
            Fraud Prevention helps protect your store from fake orders, spam
            submissions, abusive customers, and high-risk traffic before they
            impact your business. It allows you to block specific countries,
            block individual IP addresses, limit order quantities, and reduce
            repeated Cash on Delivery (COD) abuse.
          </p>
        </header>

        {/* Where to Find It */}
        <section id="where-to-find" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Where to Find It
          </h2>
          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Go to:{" "}
              <span className="font-semibold text-systemTitle">
                Settings → Fraud Prevention
              </span>
            </p>
          </div>
        </section>

        {/* Available Protections */}
        <section id="available-protections" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Available Protections
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-6">

              {/* Block Countries */}
              <div id="block-countries" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Block Countries
                </h3>
                <p className="text-sm text-systemLabel">
                  Prevent visitors from specific countries from accessing your
                  store or placing orders.
                </p>
                <p className="text-sm text-systemLabel mt-2 font-medium">
                  Use this when:
                </p>
                <ul className="text-sm text-systemLabel list-disc list-inside space-y-1 mt-1">
                  <li>You do not ship to certain countries</li>
                  <li>You are receiving fraudulent traffic from specific regions</li>
                  <li>You want to restrict store access to your target markets</li>
                </ul>
              </div>

              {/* Block IP Addresses */}
              <div id="block-ip" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Block IP Addresses
                </h3>
                <p className="text-sm text-systemLabel">
                  Block individual visitors based on their IP address.
                </p>
                <p className="text-sm text-systemLabel mt-2 font-medium">
                  Use this when:
                </p>
                <ul className="text-sm text-systemLabel list-disc list-inside space-y-1 mt-1">
                  <li>A customer repeatedly places fake orders</li>
                  <li>You identify suspicious behavior</li>
                  <li>You want to prevent a visitor from accessing your store again</li>
                </ul>
                <p className="text-sm text-systemLabel mt-2">
                  You can manually block an IP from the Fraud Prevention
                  settings or directly from an order.
                </p>
              </div>

              {/* Maximum Items Per Order */}
              <div id="max-items" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Maximum Items Per Order
                </h3>
                <p className="text-sm text-systemLabel">
                  Limit the number of products a customer can purchase in a
                  single order.
                </p>
                <p className="text-sm text-systemLabel mt-2 font-medium">
                  Use this when:
                </p>
                <ul className="text-sm text-systemLabel list-disc list-inside space-y-1 mt-1">
                  <li>You want to prevent bulk abuse</li>
                  <li>You want to reduce inventory manipulation</li>
                  <li>You want to limit unusually large orders</li>
                </ul>
              </div>

              {/* COD Delay Protection */}
              <div id="cod-delay" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Cash on Delivery (COD) Delay Protection
                </h3>
                <p className="text-sm text-systemLabel">
                  Prevent customers from placing multiple COD orders within a
                  short period of time.
                </p>
                <p className="text-sm text-systemLabel mt-2 font-medium">
                  Use this when:
                </p>
                <ul className="text-sm text-systemLabel list-disc list-inside space-y-1 mt-1">
                  <li>Customers repeatedly submit fake COD orders</li>
                  <li>You want to reduce COD abuse</li>
                  <li>You need time between order submissions from the same visitor</li>
                </ul>
                <p className="text-sm text-systemLabel mt-2">
                  You can also customize the message displayed to customers when
                  the delay rule is triggered.
                </p>
              </div>

              {/* Customer IP Tracking */}
              <div id="ip-tracking" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Customer IP Tracking
                </h3>
                <p className="text-sm text-systemLabel mb-2">
                  Every order automatically stores:
                </p>
                <ul className="text-sm text-systemLabel list-disc list-inside space-y-1">
                  <li>Customer IP address</li>
                  <li>Customer IP country</li>
                </ul>
                <p className="text-sm text-systemLabel mt-2 mb-2">
                  This information helps you:
                </p>
                <ul className="text-sm text-systemLabel list-disc list-inside space-y-1">
                  <li>Identify suspicious customers</li>
                  <li>Investigate fraudulent orders</li>
                  <li>Block abusive visitors directly from the order details page</li>
                </ul>
              </div>

            </div>
          </div>
        </section>

        {/* What Happens When a Rule Is Triggered */}
        <section id="rule-triggered" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            What Happens When a Rule Is Triggered?
          </h2>
          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-2">
              Depending on the rule:
            </p>
            <ul className="text-sm text-systemLabel list-disc list-inside space-y-1">
              <li>Visitors may be blocked from accessing your store</li>
              <li>Orders may be rejected before they are created</li>
              <li>COD orders may be temporarily prevented</li>
            </ul>
            <p className="text-sm text-systemLabel mt-3">
              Customers will see an error message explaining that their order
              cannot be completed at that time.
            </p>
          </div>
        </section>

        {/* Best Practices */}
        <section id="best-practices" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Best Practices
          </h2>
          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="text-sm text-systemLabel list-disc list-inside space-y-1">
              <li>Block countries you do not serve.</li>
              <li>Review suspicious orders regularly.</li>
              <li>Block abusive IP addresses immediately.</li>
              <li>Use COD delay protection if your store receives a high volume of fake COD orders.</li>
              <li>Set a reasonable maximum items per order limit based on your product catalog.</li>
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Frequently Asked Questions
          </h2>
          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Will blocking a country stop visitors from accessing my store?
                </h3>
                <p className="text-sm text-systemLabel">
                  Yes. Blocked countries cannot access your storefront or place orders.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Will blocked IP addresses be able to place orders?
                </h3>
                <p className="text-sm text-systemLabel">
                  No. Blocked IPs are prevented from accessing the store and completing purchases.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Can I unblock a country or IP later?
                </h3>
                <p className="text-sm text-systemLabel">
                  Yes. You can disable or remove any blocked country or IP at any time.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Does Fraud Prevention affect online payments and COD orders?
                </h3>
                <p className="text-sm text-systemLabel">
                  Blocked countries and blocked IPs apply to all orders. COD delay
                  protection only applies to Cash on Delivery orders.
                </p>
              </div>

            </div>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                By actively configuring Fraud Prevention in xPage, you protect
                your revenue, reduce operational overhead from fake orders, and
                create a safer shopping environment for your legitimate customers.
              </p>
            </div>
          </div>
        </section>

      </div>

      {/* Table of Contents Sidebar */}
      {/* Table of Contents Sidebar */}
<div className="lg:w-64 flex-shrink-0 lg:block hidden">
  <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
    <h3 className="text-systemTitle text-sm font-semibold mb-3">
      On this page
    </h3>

    <ul className="text-systemLabel text-sm space-y-2">
      <li>
        <a
          href="#overview"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "overview"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          Overview
        </a>
      </li>

      <li>
        <a
          href="#where-to-find"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "where-to-find"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          Where to Find It
        </a>
      </li>

      <li>
        <a
          href="#available-protections"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "available-protections"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          Available Protections
        </a>
      </li>

      <li>
        <a
          href="#block-countries"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "block-countries"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          Block Countries
        </a>
      </li>

      <li>
        <a
          href="#block-ip"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "block-ip"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          Block IP Addresses
        </a>
      </li>

      <li>
        <a
          href="#max-items"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "max-items"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          Maximum Items Per Order
        </a>
      </li>

      <li>
        <a
          href="#cod-delay"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "cod-delay"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          COD Delay Protection
        </a>
      </li>

      <li>
        <a
          href="#ip-tracking"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "ip-tracking"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          Customer IP Tracking
        </a>
      </li>

      <li>
        <a
          href="#rule-triggered"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "rule-triggered"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          When a Rule Is Triggered
        </a>
      </li>

      <li>
        <a
          href="#best-practices"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "best-practices"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          Best Practices
        </a>
      </li>

      <li>
        <a
          href="#faq"
          className={`block py-1 transition-colors border-l-2 pl-3 ${
            activeSection === "faq"
              ? "text-deepPurple font-semibold border-deepPurple"
              : "border-transparent hover:text-deepPurple"
          }`}
        >
          FAQ
        </a>
      </li>
    </ul>
  </div>
</div>
    </div>
  );
};

export default FraudPrevention;